// @spec RSK-STR-022
// Pending approvals page for risk approval workflow
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle, RefreshCw } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePendingApprovals } from '@/features/rsk/api/use-risk-approval';
import { BulkApprovalDialog, PendingApprovalsList } from '@/features/rsk/components';
import type { RiskCategory, RiskLevel } from '@/features/rsk/types';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/risks/approvals/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.pendingRiskApprovals'),
  }),
  component: PendingApprovalsPage,
});

/**
 * Page for managing pending risk approvals.
 *
 * Shows a list of risks awaiting approval with filtering and bulk approval capabilities.
 */
function PendingApprovalsPage() {
  const navigate = useNavigate();

  // Filter state
  const [riskLevelFilter, setRiskLevelFilter] = React.useState<RiskLevel | null>(null);
  const [categoryFilter, setCategoryFilter] = React.useState<RiskCategory | null>(null);
  const [page, setPage] = React.useState(1);

  // Selection state for bulk mode
  const [bulkMode, setBulkMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = React.useState(false);

  // Fetch pending approvals
  const {
    data: approvalsData,
    isLoading,
    refetch,
  } = usePendingApprovals({
    page,
    pageSize: 20,
    riskLevel: riskLevelFilter,
    category: categoryFilter,
  });

  const handleRefresh = () => {
    void refetch();
    setSelectedIds([]);
  };

  const handleSelectRisk = (riskId: string) => {
    void navigate({ to: '/risks/$riskId', params: { riskId } });
  };

  const handleToggleBulkMode = () => {
    setBulkMode(!bulkMode);
    setSelectedIds([]);
  };

  const handleOpenBulkDialog = () => {
    if (selectedIds.length > 0) {
      setIsBulkDialogOpen(true);
    }
  };

  const handleBulkSuccess = () => {
    setIsBulkDialogOpen(false);
    setSelectedIds([]);
    void refetch();
  };

  const handleRiskLevelFilterChange = (level: RiskLevel | null) => {
    setRiskLevelFilter(level);
    setPage(1);
    setSelectedIds([]);
  };

  const handleCategoryFilterChange = (category: RiskCategory | null) => {
    setCategoryFilter(category);
    setPage(1);
    setSelectedIds([]);
  };

  const pendingCount = approvalsData?.pagination.totalItems ?? 0;
  const items = approvalsData?.items ?? [];

  // Get selected risks for bulk dialog
  const selectedRisks = items.filter((item) => selectedIds.includes(item.riskId));

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => void navigate({ to: '/risks' })}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Risk Register
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Page Title */}
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-foreground">Pending Risk Approvals</h1>
            {pendingCount > 0 && (
              <Badge variant="secondary" className="text-lg">
                {pendingCount}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            Review and approve risk submissions. Only low-risk items can be bulk approved.
          </p>
        </div>

        {/* Bulk Actions Toolbar */}
        <div className="flex items-center justify-between rounded-lg border bg-card p-4">
          <div className="flex items-center gap-4">
            <Button
              variant={bulkMode ? 'secondary' : 'outline'}
              size="sm"
              onClick={handleToggleBulkMode}
            >
              {bulkMode ? 'Exit Bulk Mode' : 'Enable Bulk Mode'}
            </Button>
            {bulkMode && selectedIds.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected
              </span>
            )}
          </div>
          {bulkMode && (
            <Button onClick={handleOpenBulkDialog} disabled={selectedIds.length === 0}>
              <CheckCircle className="mr-2 h-4 w-4" />
              Bulk Approve ({selectedIds.length})
            </Button>
          )}
        </div>

        {/* Approvals List */}
        <PendingApprovalsList
          items={items}
          isLoading={isLoading}
          onSelectRisk={handleSelectRisk}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          bulkMode={bulkMode}
          onRefresh={handleRefresh}
          riskLevelFilter={riskLevelFilter}
          onRiskLevelFilterChange={handleRiskLevelFilterChange}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={handleCategoryFilterChange}
        />

        {/* Pagination */}
        {approvalsData && approvalsData.pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => {
                setPage((p) => p - 1);
              }}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {approvalsData.pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= approvalsData.pagination.totalPages}
              onClick={() => {
                setPage((p) => p + 1);
              }}
            >
              Next
            </Button>
          </div>
        )}

        {/* Bulk Approval Dialog */}
        <BulkApprovalDialog
          isOpen={isBulkDialogOpen}
          onClose={() => {
            setIsBulkDialogOpen(false);
          }}
          riskIds={selectedIds}
          risks={selectedRisks}
          onSuccess={handleBulkSuccess}
        />
      </div>
    </div>
  );
}
